import { useState } from "react";
import { useForm } from "react-hook-form";

import type { Agent, AgentMessage } from "@alaffia-technology-solutions/schema";

import { AgentMessageUserValidationStatus } from "@alaffia-technology-solutions/client-sdk";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toggle,
} from "@alaffia-technology-solutions/ds";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Circle, ThumbsDown } from "lucide-react";
import { z } from "zod";

import {
  useAcceptAgentMessage,
  useUnsetAgentMessageUserValidation,
} from "../hooks";

type UserValidationButtonProps = UseUserValidationButtonOptions;

export function UserValidationButton({
  agentId,
  agentMessage,
}: UserValidationButtonProps) {
  const { isAccepted, label, ...props } = useUserValidationButton({
    agentId,
    agentMessage,
  });
  const Icon = isAccepted ? Check : Circle;

  return (
    <Toggle
      pressed={isAccepted}
      variant={isAccepted ? "select" : "outline"}
      data-testid="agent-accept-cta"
      {...props}
    >
      <Icon className="af-h-4 af-w-4" />
      {label}
    </Toggle>
  );
}

interface UseUserValidationButtonOptions {
  agentId: Agent["id"];
  agentMessage: AgentMessage | undefined | null;
}

function useUserValidationButton({
  agentId,
  agentMessage,
}: UseUserValidationButtonOptions) {
  const acceptAgentMessage = useAcceptAgentMessage({ agentId });
  const unsetAgentMessageUserValidation = useUnsetAgentMessageUserValidation({
    agentId,
  });

  const isAccepted =
    agentMessage?.userValidation?.status ===
    AgentMessageUserValidationStatus.ACCEPTED;

  const onClick = () => {
    if (!agentMessage) {
      return;
    }

    if (isAccepted) {
      unsetAgentMessageUserValidation.mutate({
        agentMessageId: agentMessage.id,
        revisionId: agentMessage.revision?.revisionId,
      });
    } else {
      acceptAgentMessage.mutate({
        agentMessageId: agentMessage.id,
        revisionId: agentMessage.revision?.revisionId,
      });
    }
  };

  return {
    onClick,
    isAccepted,
    disabled:
      !agentMessage ||
      acceptAgentMessage.isPending ||
      unsetAgentMessageUserValidation.isPending,
    label: isAccepted ? "Accepted" : "Accept",
  };
}

const rejectFormSchema = z.object({
  rejectionReason: z.string(),
});

type RejectFormData = z.infer<typeof rejectFormSchema>;

interface RejectFormProps {
  onSubmit: (data: RejectFormData) => void;
}

export function RejectForm({ onSubmit }: RejectFormProps) {
  const [rejectPopoverOpen, onRejectPopoverOpenChange] = useState(false);
  const disabled = false;

  return (
    <Popover onOpenChange={onRejectPopoverOpenChange} open={rejectPopoverOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button disabled={disabled} size="sm" variant="destructive">
          <ThumbsDown className="af-h-4 af-w-4" />
          Decline
        </Button>
      </PopoverTrigger>
      {rejectPopoverOpen && (
        <RejectPopoverContent
          onClose={() => onRejectPopoverOpenChange(false)}
          onSubmit={onSubmit}
        />
      )}
    </Popover>
  );
}

const rejectionReasons = [
  {
    label:
      "The content was incomplete and was missing information from the source",
    value:
      "The content was incomplete and was missing information from the source",
  },
  {
    label:
      "The content was inaccurate and documented differently in the source",
    value:
      "The content was inaccurate and documented differently in the source",
  },
  {
    label: "The content was not found in the source",
    value: "The content was not found in the source",
  },
];

interface RejectPopoverContentProps {
  onClose: () => void;
  onSubmit: (data: RejectFormData) => void;
}

const RejectPopoverContent = ({
  onClose,
  onSubmit,
}: RejectPopoverContentProps) => {
  const form = useForm<RejectFormData>({
    resolver: zodResolver(rejectFormSchema),
  });

  return (
    <PopoverContent
      className="af-border-none af-bg-transparent af-p-0 af-shadow-none"
      side="top"
      sideOffset={20}
    >
      <Card className="af-w-[360px]">
        <CardHeader className="af-px-5 af-pb-4 af-pt-5">
          <CardTitle className="af-text-lg">Decline task</CardTitle>
          <CardDescription>
            Please tell us why this task should be rejected.
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent className="af-px-5 af-pb-4">
              <FormField
                control={form.control}
                name="rejectionReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="af-font-medium">
                      Choose reason
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {rejectionReasons.map(({ label, value }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="af-flex af-justify-between af-px-5 af-pb-5">
              <Button variant="outline" onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button
                disabled={!form.formState.isValid}
                variant="destructive"
                size="sm"
                type="submit"
              >
                Decline Task
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </PopoverContent>
  );
};
